import * as React from 'react'

import { Button, OptionSelector } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import useSearch from 'modules/core/hooks/useSearch'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import questionCategories from 'modules/surveys/store/collections/questionCategories'
import questionConfigs from 'modules/surveys/store/collections/questionConfigs'
import questionsTemplates from 'modules/surveys/store/collections/questionsTemplates'
import { FormType } from 'modules/surveys/store/models/Form'
import QuestionGroupConfig from 'modules/surveys/store/models/QuestionGroupConfig'

interface Props {
  surveyId: string
  onClose: () => void
  questionGroupConfigId: string
}

function ApplyTemplateModal({ surveyId, questionGroupConfigId, onClose }: Props) {
  const [selectedQuestionsTemplateId, setQuestionsTemplateId] = React.useState<string | null>(null)

  const [submitLoading, setSubmitLoading] = React.useState(false)
  const { search } = useSearch()
  const { loading } = useAsyncData(() => questionsTemplates.fetch(), [])
  const { updateCanBeStarted } = useSurveyShowContext()

  const submit = async (closePortal: ClosePortalFn) => {
    if (!selectedQuestionsTemplateId) return

    setSubmitLoading(true)

    const questionGroupConfig = new QuestionGroupConfig({
      id: questionGroupConfigId,
    })

    const { success } = await asyncAction(() =>
      questionGroupConfig.rpc('apply_questions_template', {
        surveys_questions_template_id: selectedQuestionsTemplateId,
        surveys_form_id: surveyId,
      })
    )

    if (success) {
      await Promise.all([
        questionConfigs.fetch({ data: { surveys_form_id: surveyId } }),
        questionCategories.fetch({ data: { surveys_form_id: surveyId } }),
      ])
      await updateCanBeStarted()
      closePortal()
    }

    setSubmitLoading(false)
  }

  const filteredQuestionsTemplates = questionsTemplates.models.filter(
    (questionsTemplate) =>
      questionsTemplate.get('type') === FormType.Survey &&
      matchText(search, questionsTemplate.get('title'))
  )

  const items = filteredQuestionsTemplates.map((questionTemplate) => ({
    name: questionTemplate.attributes.title,
    value: questionTemplate.attributes.id,
  }))

  const running = loading || submitLoading

  return (
    <Modal
      illustration="template"
      title={i18n.t('surveys.form.questions_template.title')}
      description={i18n.t('surveys.form.questions_template.description')}
      onSubmit={submit}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" disabled={!selectedQuestionsTemplateId || submitLoading} submit>
            {i18n.t('surveys.form.questions_template.add_questions')}
          </Button>
        </>
      }
    >
      {running && <Loading />}
      {!running && (
        <OptionSelector
          selectedValue={selectedQuestionsTemplateId}
          items={items}
          noItems={i18n.t('common.templates_not_found')}
          onSelect={(item) => item.value && setQuestionsTemplateId(String(item.value))}
        />
      )}
    </Modal>
  )
}

export default observer(ApplyTemplateModal)
